import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, ModalContent, ModalHeader, ModalFooter } from '@/components/ui/Modal'
import { Button } from '@/components/base/buttons'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import { Badge } from '@/components/ui/Badge'
import {
  PlayIcon,
  Stars02Icon,
  SearchMdIcon,
  CheckCircleIcon,
  Tool02Icon,
  MessageTextCircle02Icon,
  RefreshCcw01Icon,
} from '@untitledui-icons/react/line'
import { useSimScenarios, useSimTags, useRunSimulation } from '@/hooks/api/useSimulations'
import { useAIDeployments } from '@/hooks/api/useAIDeployments'
import { useAssistantsByIds } from '@/hooks/api/useAssistants'
import { AsyncMultiSelect, type SelectOption } from '@/components/forms/AsyncMultiSelect'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { SimBatch, SimScenario } from '@/types/simulation'

const ITERATION_OPTIONS = [
  { value: '1', label: '1x' },
  { value: '3', label: '3x' },
  { value: '5', label: '5x' },
  { value: '10', label: '10x' },
]

interface RunSimulationDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  /** Optional batch to re-run - pre-populates the form with batch settings */
  rerunBatch?: SimBatch | null
}

/**
 * Improved Run Simulation dialog with better UX:
 * - Two-panel layout for category/scenario selection
 * - Visual scenario cards with check indicators
 * - Collapsible advanced options
 * - Inline validation
 * - "Run again" mode: pre-populate from an existing batch
 */
export function RunSimulationDialog({ isOpen, onClose, onSuccess, rerunBatch }: RunSimulationDialogProps) {
  const { t } = useTranslation()

  // Form state
  const [selectedAssistants, setSelectedAssistants] = useState<SelectOption[]>([])
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set())
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<Set<number>>(new Set())
  const [iterations, setIterations] = useState<number>(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [tagSearchQuery, setTagSearchQuery] = useState('')
  const [aiDeploymentOverride, setAiDeploymentOverride] = useState<number | null>(null)
  const [selectedSimulatorIds, setSelectedSimulatorIds] = useState<number[]>([])
  const [selectedJudgeIds, setSelectedJudgeIds] = useState<number[]>([])
  const [isInitialized, setIsInitialized] = useState(false)

  // Fetch scenarios, tags, and AI deployments
  const { data: scenarios = [] } = useSimScenarios({})
  const { data: availableTags = [] } = useSimTags()
  const { data: aiDeployments = [] } = useAIDeployments()
  const runSimulation = useRunSimulation()

  // Convert AI deployments to SelectOption format for AsyncMultiSelect
  const aiDeploymentOptions = useMemo(
    () =>
      aiDeployments.map((d) => ({
        id: d.id,
        name: d.friendly_name || `${d.vendor} - ${d.model}`,
      })),
    [aiDeployments]
  )

  // Convert selected IDs to SelectOption objects
  const selectedSimulatorOptions = useMemo(
    () => aiDeploymentOptions.filter((opt) => selectedSimulatorIds.includes(opt.id)),
    [aiDeploymentOptions, selectedSimulatorIds]
  )
  const selectedJudgeOptions = useMemo(
    () => aiDeploymentOptions.filter((opt) => selectedJudgeIds.includes(opt.id)),
    [aiDeploymentOptions, selectedJudgeIds]
  )

  // Fetch assistant details for rerun mode
  const rerunAssistantIds = rerunBatch?.assistant_ids || []
  const { data: rerunAssistants = [] } = useAssistantsByIds(
    rerunAssistantIds,
    isOpen && rerunAssistantIds.length > 0
  )

  // Determine if this is a "Run again" mode
  const isRerunMode = Boolean(rerunBatch)

  // Pre-populate form when opening in "Run again" mode
  useEffect(() => {
    if (isOpen && rerunBatch && scenarios.length > 0 && !isInitialized) {
      // Pre-select scenarios from the batch
      setSelectedScenarioIds(new Set(rerunBatch.scenario_ids || []))
      
      // Set iterations
      setIterations(rerunBatch.iterations || 1)
      
      // Set AI deployment override
      if (rerunBatch.ai_deployment_id_override) {
        setAiDeploymentOverride(rerunBatch.ai_deployment_id_override)
      }
      
      // Set simulator and judge model overrides
      if (rerunBatch.simulator_ai_deployment_ids?.length > 0) {
        setSelectedSimulatorIds(rerunBatch.simulator_ai_deployment_ids)
      }
      if (rerunBatch.judge_ai_deployment_ids?.length > 0) {
        setSelectedJudgeIds(rerunBatch.judge_ai_deployment_ids)
      }
      
      setIsInitialized(true)
    }
    
    // Reset initialization flag when dialog closes
    if (!isOpen) {
      setIsInitialized(false)
    }
  }, [isOpen, rerunBatch, scenarios, isInitialized])

  // Update selected assistants when assistant data is fetched
  useEffect(() => {
    if (isOpen && rerunAssistants.length > 0 && isInitialized) {
      setSelectedAssistants(
        rerunAssistants.map((assistant) => ({
          id: assistant.id,
          name: assistant.name,
        }))
      )
    }
  }, [isOpen, rerunAssistants, isInitialized])

  // Count scenarios by tag
  const tagCounts = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const scenario of scenarios) {
      for (const tag of scenario.tags || []) {
        counts[tag.id] = (counts[tag.id] || 0) + 1
      }
    }
    return counts
  }, [scenarios])

  // Filter tags by search and exclude empty tags
  const filteredTags = useMemo(() => {
    return availableTags.filter((tag) => {
      // Exclude tags with no scenarios
      if ((tagCounts[tag.id] || 0) === 0) return false
      // Filter by search query
      if (tagSearchQuery.trim()) {
        return tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
      }
      return true
    })
  }, [availableTags, tagCounts, tagSearchQuery])

  // Filter scenarios by tags and search
  const filteredScenarios = useMemo(() => {
    let result = scenarios
    if (selectedTagIds.size > 0) {
      result = result.filter((s) =>
        s.tags?.some((tag) => selectedTagIds.has(tag.id))
      )
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.tags?.some((tag) => tag.name.toLowerCase().includes(query))
      )
    }
    return result
  }, [scenarios, selectedTagIds, searchQuery])

  // Calculate what will run - only explicitly selected scenarios
  const scenariosToRun = selectedScenarioIds.size

  // Calculate total runs including model combinations
  const simulatorMultiplier = Math.max(selectedSimulatorIds.length, 1)
  const judgeMultiplier = Math.max(selectedJudgeIds.length, 1)
  const totalRuns = scenariosToRun * iterations * Math.max(selectedAssistants.length, 1) * simulatorMultiplier * judgeMultiplier

  // Validation state - require explicit scenario selection
  const isValid = useMemo(() => {
    if (selectedAssistants.length === 0) return false
    if (selectedScenarioIds.size === 0) return false
    return true
  }, [selectedAssistants, selectedScenarioIds])

  // Handlers
  const handleTagToggle = useCallback((tagId: number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
    setSelectedScenarioIds(new Set()) // Reset selections when changing tags
  }, [])

  const handleClearTags = useCallback(() => {
    setSelectedTagIds(new Set())
    setSelectedScenarioIds(new Set())
  }, [])

  const handleScenarioToggle = useCallback((scenarioId: number) => {
    setSelectedScenarioIds((prev) => {
      const next = new Set(prev)
      if (next.has(scenarioId)) {
        next.delete(scenarioId)
      } else {
        next.add(scenarioId)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    const allIds = filteredScenarios.map((s) => s.id)
    const allSelected = allIds.every((id) => selectedScenarioIds.has(id))
    if (allSelected) {
      setSelectedScenarioIds(new Set())
    } else {
      setSelectedScenarioIds(new Set(allIds))
    }
  }, [filteredScenarios, selectedScenarioIds])

  const handleRunSimulation = useCallback(async () => {
    if (selectedAssistants.length === 0) {
      toast.error(t('pages.simulations.run_modal.error_no_assistants'))
      return
    }

    try {
      await runSimulation.mutateAsync({
        assistant_ids: selectedAssistants.map((a) => a.id),
        scenario_ids: Array.from(selectedScenarioIds),
        iterations,
        ai_deployment_id_override: aiDeploymentOverride,
        simulator_ai_deployment_ids: selectedSimulatorIds.length > 0 ? selectedSimulatorIds : null,
        judge_ai_deployment_ids: selectedJudgeIds.length > 0 ? selectedJudgeIds : null,
      })
      toast.success(t('pages.simulations.run_modal.success'))

      // Reset form
      setSelectedAssistants([])
      setSelectedTagIds(new Set())
      setSelectedScenarioIds(new Set())
      setIterations(1)
      setSearchQuery('')
      setTagSearchQuery('')
      setAiDeploymentOverride(null)
      setSelectedSimulatorIds([])
      setSelectedJudgeIds([])

      onClose()
      onSuccess?.()
    } catch {
      toast.error(t('pages.simulations.run_modal.error'))
    }
  }, [selectedAssistants, selectedScenarioIds, iterations, aiDeploymentOverride, selectedSimulatorIds, selectedJudgeIds, runSimulation, onClose, onSuccess, t])

  const handleClose = useCallback(() => {
    // Reset form on close
    setSelectedAssistants([])
    setSelectedTagIds(new Set())
    setSelectedScenarioIds(new Set())
    setIterations(1)
    setSearchQuery('')
    setTagSearchQuery('')
    setAiDeploymentOverride(null)
    setSelectedSimulatorIds([])
    setSelectedJudgeIds([])
    onClose()
  }, [onClose])

  // Render scenario card
  const renderScenarioCard = (scenario: SimScenario) => {
    const isSelected = selectedScenarioIds.has(scenario.id)
    const hasToolCheck = scenario.expected_tools && scenario.expected_tools.length > 0
    const hasResponseCheck =
      (scenario.must_contain && scenario.must_contain.length > 0) ||
      (scenario.must_not_contain && scenario.must_not_contain.length > 0)
    const hasLlmJudge = !!scenario.llm_judge_criteria

    return (
      <div
        key={scenario.id}
        className={cn(
          'flex items-start gap-3 rounded-lg border p-3 transition-colors cursor-pointer',
          'hover:bg-gray-50 dark:hover:bg-gray-800/50',
          isSelected
            ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/30'
            : 'border-gray-200 dark:border-gray-700'
        )}
        onClick={() => handleScenarioToggle(scenario.id)}
        role="checkbox"
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleScenarioToggle(scenario.id)
          }
        }}
      >
        <Checkbox
          isSelected={isSelected}
          onChange={() => handleScenarioToggle(scenario.id)}
          aria-label={t('pages.simulations.run_modal.select_scenario_label', { name: scenario.name })}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {scenario.name}
            </span>
            {scenario.tags?.map((tag) => (
              <Badge key={tag.id} size="sm" variant="gray">
                {tag.name}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            {hasToolCheck && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Tool02Icon className="h-3 w-3" />
                {t('pages.simulations.run_modal.check_tools')}
              </span>
            )}
            {hasResponseCheck && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <MessageTextCircle02Icon className="h-3 w-3" />
                {t('pages.simulations.run_modal.check_response')}
              </span>
            )}
            {hasLlmJudge && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <CheckCircleIcon className="h-3 w-3" />
                {t('pages.simulations.run_modal.check_llm')}
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  const allFiltered = filteredScenarios.map((s) => s.id)
  const allSelected = allFiltered.length > 0 && allFiltered.every((id) => selectedScenarioIds.has(id))

  return (
    <Modal isOpen={isOpen} isDismissable onOpenChange={(open) => !open && handleClose()} size="5xl">
      <ModalContent className="flex flex-col overflow-hidden h-full">
        <ModalHeader onClose={handleClose}>
          {isRerunMode
            ? t('pages.simulations.run_modal.title_rerun', { batchId: rerunBatch?.id })
            : t('pages.simulations.run_modal.title')}
        </ModalHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Two-column layout */}
          <div className="flex gap-6">
            {/* Left column: Assistants + Categories + Iterations */}
            <div className="w-64 shrink-0 space-y-6">
              {/* Step 1: Select Assistants */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                    1
                  </span>
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('pages.simulations.run_modal.step_assistants')}
                  </label>
                </div>
                <AsyncMultiSelect
                  placeholder={t('pages.simulations.run_modal.assistants_placeholder')}
                  searchPlaceholder={t('resources.dashboard.filters.search_assistants')}
                  searchEndpoint="/assistants"
                  value={selectedAssistants}
                  onChange={setSelectedAssistants}
                  emptyMessage={t('resources.dashboard.filters.no_results')}
                  icon={Stars02Icon}
                />
                {selectedAssistants.length === 0 && (
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    {t('pages.simulations.run_modal.assistants_hint')}
                  </p>
                )}
              </div>

              {/* Tag filter */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('pages.simulations.run_modal.filter_by_tags')}
                  </label>
                  {selectedTagIds.size > 0 && (
                    <button
                      type="button"
                      onClick={handleClearTags}
                      className="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                    >
                      {t('pages.simulations.run_modal.clear_tags')}
                    </button>
                  )}
                </div>

                {/* Tag search input */}
                <div className="relative mb-2">
                  <SearchMdIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={tagSearchQuery}
                    onChange={(e) => setTagSearchQuery(e.target.value)}
                    placeholder={t('pages.simulations.run_modal.search_tags')}
                    className={cn(
                      'w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-9 pr-3 text-sm',
                      'placeholder:text-gray-400',
                      'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
                      'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100'
                    )}
                  />
                </div>

                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {filteredTags.map((tag) => {
                    const isSelected = selectedTagIds.has(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleTagToggle(tag.id)}
                        className={cn(
                          'w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                          isSelected
                            ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded border',
                            isSelected
                              ? 'border-brand-600 bg-brand-600 dark:border-brand-500 dark:bg-brand-500'
                              : 'border-gray-300 dark:border-gray-600'
                          )}
                        >
                          {isSelected && (
                            <CheckCircleIcon className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span className="flex-1 text-left">{tag.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {tagCounts[tag.id] || 0}
                        </span>
                      </button>
                    )
                  })}
                  {filteredTags.length === 0 && tagSearchQuery.trim() && (
                    <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      {t('pages.simulations.run_modal.no_tags_found')}
                    </p>
                  )}
                </div>
              </div>

              {/* Iterations */}
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('pages.simulations.run_modal.iterations')}
                </label>
                <Select
                  options={ITERATION_OPTIONS}
                  selectedKey={String(iterations)}
                  onSelectionChange={(key) => setIterations(Number(key) || 1)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('pages.simulations.run_modal.iterations_hint')}
                </p>
              </div>
            </div>

            {/* Right column: Scenarios */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                  2
                </span>
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('pages.simulations.run_modal.step_scenarios')}
                </label>
              </div>

              {/* Search and select all */}
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <SearchMdIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('pages.simulations.run_modal.search_scenarios')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn(
                      'w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm',
                      'placeholder:text-gray-400',
                      'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
                      'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
                    )}
                  />
                </div>
                {filteredScenarios.length > 1 && (
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {allSelected
                      ? t('pages.simulations.run_modal.deselect_all')
                      : t('pages.simulations.run_modal.select_all')}
                  </Button>
                )}
              </div>

              {/* Scenario list */}
              <div className="h-80 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/30">
                {filteredScenarios.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {searchQuery
                      ? t('pages.simulations.run_modal.no_scenarios_match')
                      : t('pages.simulations.run_modal.no_scenarios')}
                  </div>
                ) : (
                  filteredScenarios.map(renderScenarioCard)
                )}
              </div>

              {/* Selection hint */}
              <p className={cn(
                'mt-2 text-xs',
                selectedScenarioIds.size > 0
                  ? 'text-gray-500 dark:text-gray-400'
                  : 'text-warning-600 dark:text-warning-400'
              )}>
                {selectedScenarioIds.size > 0
                  ? t('pages.simulations.run_modal.scenarios_selected', {
                      count: selectedScenarioIds.size,
                    })
                  : t('pages.simulations.run_modal.no_scenarios_selected')}
              </p>

              {/* Summary */}
              <div className="mt-4 rounded-lg bg-gray-100 p-4 dark:bg-gray-800/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('pages.simulations.run_modal.summary_title')}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('pages.simulations.run_modal.summary_detail', {
                        scenarios: scenariosToRun,
                        iterations,
                        assistants: selectedAssistants.length || 1,
                        total: totalRuns,
                      })}
                    </p>
                    {selectedAssistants.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedAssistants.map((a) => (
                          <Badge key={a.id} size="sm" variant="brand">
                            {a.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {totalRuns}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('pages.simulations.run_modal.total_runs')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Model Overrides - Full width section */}
          <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('pages.simulations.run_modal.model_overrides_section')}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {/* AI Deployment Override (Assistant) */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('pages.simulations.run_modal.ai_deployment_override')}
                </label>
                <Select
                  options={[
                    { value: '', label: t('pages.simulations.run_modal.ai_deployment_default') },
                    ...aiDeployments.map((d) => ({
                      value: String(d.id),
                      label: d.friendly_name || `${d.vendor} - ${d.model}`,
                    })),
                  ]}
                  selectedKey={aiDeploymentOverride ? String(aiDeploymentOverride) : ''}
                  onSelectionChange={(key) =>
                    setAiDeploymentOverride(key === '' ? null : Number(key))
                  }
                />
              </div>

              {/* Simulator Models (Multi-select) */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('pages.simulations.run_modal.simulator_models')}
                </label>
                <AsyncMultiSelect
                  placeholder={t('pages.simulations.run_modal.simulator_models_placeholder')}
                  searchPlaceholder={t('resources.generic.search')}
                  staticOptions={aiDeploymentOptions}
                  value={selectedSimulatorOptions}
                  onChange={(options: SelectOption[]) =>
                    setSelectedSimulatorIds(options.map((o) => o.id))
                  }
                  emptyMessage={t('resources.dashboard.filters.no_results')}
                  icon={Stars02Icon}
                />
              </div>

              {/* Judge Models (Multi-select) */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('pages.simulations.run_modal.judge_models')}
                </label>
                <AsyncMultiSelect
                  placeholder={t('pages.simulations.run_modal.judge_models_placeholder')}
                  searchPlaceholder={t('resources.generic.search')}
                  staticOptions={aiDeploymentOptions}
                  value={selectedJudgeOptions}
                  onChange={(options: SelectOption[]) =>
                    setSelectedJudgeIds(options.map((o) => o.id))
                  }
                  emptyMessage={t('resources.dashboard.filters.no_results')}
                  icon={Stars02Icon}
                />
              </div>
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            {t('resources.generic.cancel')}
          </Button>
          <Button
            onClick={handleRunSimulation}
            isLoading={runSimulation.isPending}
            isDisabled={!isValid}
            iconLeading={isRerunMode ? RefreshCcw01Icon : PlayIcon}
          >
            {isRerunMode
              ? t('pages.simulations.run_modal.run_again')
              : t('pages.simulations.run_modal.run')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RunSimulationDialog
